// /library and /recent slash-command targets for the chat shell.
//
// Goal: keep the chat the *single* place users live, and surface what
// used to be a separate Library page (filters + featured/trending/newest
// shelves) AND the recent-chat shelf as actions you launch with a
// command. The page at /library still exists for SEO + shareable
// links, but power users never have to leave the chat to browse it.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Library as LibraryIcon, Star, Flame, Clock, MessagesSquare, ExternalLink, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import LibraryEntryRow from "@/components/library/LibraryEntryRow";
import LibraryFiltersPanel, { type LibraryFilterState } from "@/components/library/LibraryFiltersPanel";
import { useLibraryEntries, type LibraryKind } from "@/hooks/useLibrary";
import { useFavorites } from "@/hooks/useFavorites";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { detectChatType, type ChatType } from "@/lib/chatType";
import { toast } from "sonner";

const KIND_FILTERS: { value: LibraryKind | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "guide", label: "Guides" },
  { value: "research", label: "Research" },
  { value: "prompt", label: "Prompts" },
  { value: "jam", label: "Jams" },
  { value: "protocol_run", label: "Runs" },
];

export type ChatLibraryTab = "library" | "recent";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Which view to land on. `/library` → "library", `/recent` → "recent". */
  initialTab?: ChatLibraryTab;
  /** Optional pre-filled search term lifted from the slash command. */
  initialQuery?: string;
  /** Called when the user picks a recent chat to resume in the shell. */
  onPickRecent?: (id: string) => void;
}

const ChatLibraryDialog = ({
  open,
  onClose,
  initialTab = "library",
  initialQuery = "",
  onPickRecent,
}: Props) => {
  const [tab, setTab] = useState<ChatLibraryTab>(initialTab);
  const [search, setSearch] = useState(initialQuery);
  const [kind, setKind] = useState<LibraryKind | "all">("all");
  const [filters, setFilters] = useState<LibraryFilterState>({
    category: null,
    pricing: null,
    tags: [],
  });

  // Re-sync incoming props every time the dialog re-opens — slash commands
  // can land on a different tab or query than the last open did.
  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setSearch(initialQuery);
  }, [open, initialTab, initialQuery]);

  const { data: entries = [], isLoading } = useLibraryEntries({
    kind,
    search,
    sort: "newest",
  });
  const { favorites } = useFavorites();
  const { user } = useAuth();

  // Real recent chats live in `chat_conversations` (auto-saved by the
  // chat shell). The Recent tab is per-user, ordered newest-first, with
  // the auto-derived smart title from `deriveChatTitle` so the user can
  // identify a run at a glance — "Quest: Cold-email a founder · Apr 27 14:32".
  const [recentRows, setRecentRows] = useState<
    { id: string; title: string; teaser: string | null; updated_at: string; message_count: number; chat_type: ChatType }[]
  >([]);
  const [recentLoading, setRecentLoading] = useState(false);

  useEffect(() => {
    if (!open || tab !== "recent" || !user) return;
    let cancelled = false;
    setRecentLoading(true);
    void supabase
      .from("chat_conversations")
      .select("id, title, teaser, last_activity_at, message_count, chat_type")
      .eq("user_id", user.id)
      .order("last_activity_at", { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setRecentRows([]);
        } else {
          setRecentRows(
            data.map((r) => ({
              id: r.id,
              title: r.title || "Untitled chat",
              teaser: r.teaser ?? null,
              updated_at: r.last_activity_at,
              message_count: r.message_count ?? 0,
              // Trust the column when present; fall back to title
              // prefix detection so any pre-migration rows still
              // bucket correctly until they're updated.
              chat_type: ((r as { chat_type?: string }).chat_type as ChatType) ?? detectChatType(r.title),
            })),
          );
        }
        setRecentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tab, user]);

  const filteredEntries = useMemo(() => {
    if (filters.tags.length === 0) return entries;
    const wanted = new Set(filters.tags.map((t) => t.toLowerCase()));
    return entries.filter((e) => (e.tags ?? []).some((t) => wanted.has(t.toLowerCase())));
  }, [entries, filters.tags]);

  const featured = filteredEntries.filter((e) => e.is_featured);
  const trending = [...filteredEntries]
    .filter((e) => !e.is_featured && e.fork_count > 0)
    .sort((a, b) => b.fork_count - a.fork_count)
    .slice(0, 8);
  const newest = filteredEntries.filter(
    (e) => !e.is_featured && !trending.includes(e),
  );

  const recentItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recentRows.slice(0, 25);
    return recentRows
      .filter((p) => `${p.title} ${p.teaser ?? ""}`.toLowerCase().includes(q))
      .slice(0, 25);
  }, [recentRows, search]);

  // Bucket recent rows by detected type so the user can scan
  // "Quest" / "Challenge" / "Task" runs at a glance instead of
  // skimming a flat list. Order: Quest → Challenge → Task → Open
  // (most-tracked first).
  const groupedRecent = useMemo(() => {
    const groups: Record<ChatType, typeof recentItems> = {
      quest: [],
      challenge: [],
      task: [],
      open: [],
    };
    for (const r of recentItems) {
      groups[r.chat_type].push(r);
    }
    return groups;
  }, [recentItems]);

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-mono text-base flex items-center gap-2">
            <LibraryIcon className="w-4 h-4 text-primary" />
            {tab === "library" ? "Library" : "Recent chats"}
            <span className="ml-auto text-[10px] font-mono text-muted-foreground">
              chat command · /{tab}
            </span>
          </DialogTitle>

          <div className="flex items-center gap-1.5 mt-3">
            <Button
              size="sm"
              variant={tab === "library" ? "default" : "outline"}
              onClick={() => setTab("library")}
              className="h-7 text-[11px] font-mono"
            >
              <LibraryIcon className="w-3 h-3 mr-1.5" /> Library
            </Button>
            <Button
              size="sm"
              variant={tab === "recent" ? "default" : "outline"}
              onClick={() => setTab("recent")}
              className="h-7 text-[11px] font-mono"
            >
              <MessagesSquare className="w-3 h-3 mr-1.5" /> Recent
            </Button>
            <Link
              to="/library"
              onClick={onClose}
              className="ml-auto text-[10px] font-mono text-muted-foreground hover:text-primary inline-flex items-center gap-1"
            >
              Open full page <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </DialogHeader>

        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                tab === "library"
                  ? "Search Library entries…"
                  : "Search recent chats — try 'quest', 'challenge', 'hack'…"
              }
              className="pl-9 font-mono text-sm h-9"
            />
            {tab === "recent" && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {["quest", "challenge", "hack", "playbook"].map((kw) => (
                  <button
                    key={kw}
                    type="button"
                    onClick={() => setSearch(kw)}
                    className={cn(
                      "px-1.5 py-0.5 rounded font-mono text-[10px] border border-border bg-card/50 text-muted-foreground hover:text-primary hover:border-primary transition-colors",
                      search.toLowerCase() === kw && "border-primary text-primary",
                    )}
                  >
                    {kw}
                  </button>
                ))}
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="px-1.5 py-0.5 rounded font-mono text-[10px] text-muted-foreground hover:text-foreground"
                  >
                    clear
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {tab === "library" ? (
            <>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {KIND_FILTERS.map((f) => (
                  <Button
                    key={f.value}
                    size="sm"
                    variant={kind === f.value ? "default" : "outline"}
                    onClick={() => setKind(f.value)}
                    className="font-mono text-[11px] h-7"
                  >
                    {f.label}
                  </Button>
                ))}
              </div>

              <LibraryFiltersPanel
                state={filters}
                onChange={setFilters}
                bookmarkedSlugs={Array.from(favorites)}
                entries={entries}
              />

              {isLoading ? (
                <div className="text-sm font-mono text-muted-foreground py-10 text-center">
                  Loading library…
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="rounded-md border border-dashed border-border bg-card/50 p-8 text-center text-xs font-mono text-muted-foreground">
                  No matches. Try clearing the kind / tag filters.
                </div>
              ) : (
                <div className="space-y-6">
                  {featured.length > 0 && (
                    <Shelf
                      icon={<Star className="w-3.5 h-3.5 fill-primary text-primary" />}
                      title="HackerLabs Picks"
                      entries={featured}
                    />
                  )}
                  {trending.length > 0 && (
                    <Shelf
                      icon={<Flame className="w-3.5 h-3.5 text-primary" />}
                      title="Most forked"
                      entries={trending}
                    />
                  )}
                  {newest.length > 0 && (
                    <Shelf
                      icon={<Clock className="w-3.5 h-3.5 text-muted-foreground" />}
                      title="Newest"
                      entries={newest}
                    />
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              {(["quest", "challenge", "task", "open"] as ChatType[]).map((bucket) => {
                const items = groupedRecent[bucket];
                if (recentLoading) return null;
                if (items.length === 0) return null;
                const labelMap: Record<ChatType, string> = {
                  quest: "Quests",
                  challenge: "Challenges",
                  task: "Tasks",
                  open: "Open chats",
                };
                return (
                  <section key={bucket}>
                    <h3 className="font-mono font-semibold text-[11px] mb-1.5 text-muted-foreground uppercase tracking-wider">
                      {labelMap[bucket]}{" "}
                      <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
                        ({items.length})
                      </span>
                    </h3>
                    <RecentList
                      items={items}
                      loading={false}
                      onPick={(id) => {
                        onPickRecent?.(id);
                        onClose();
                      }}
                    />
                  </section>
                );
              })}
              {recentLoading && (
                <RecentList items={[]} loading={true} onPick={() => {}} />
              )}
              {!recentLoading &&
                (Object.values(groupedRecent) as typeof recentItems[]).every((g) => g.length === 0) && (
                  <RecentList items={[]} loading={false} onPick={() => {}} />
                )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Shelf = ({
  icon,
  title,
  entries,
}: {
  icon: React.ReactNode;
  title: string;
  entries: ReturnType<typeof useLibraryEntries>["data"] extends infer T
    ? T extends Array<infer E>
      ? E[]
      : never
    : never;
}) => (
  <section>
    <h3 className="font-mono font-semibold text-xs flex items-center gap-1.5 mb-2 text-muted-foreground uppercase tracking-wider">
      {icon} {title}
      <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
        ({entries.length})
      </span>
    </h3>
    <div className="flex flex-col gap-1.5">
      {entries.map((e) => (
        <LibraryEntryRow key={e.id} entry={e} />
      ))}
    </div>
  </section>
);

const RecentList = ({
  items,
  loading,
  onPick,
}: {
  items: { id: string; title: string; teaser?: string | null; updated_at?: string; message_count?: number }[];
  loading: boolean;
  onPick: (id: string) => void;
}) => {
  if (loading) {
    return (
      <div className="text-sm font-mono text-muted-foreground py-10 text-center">
        Loading recent chats…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 p-8 text-center text-xs font-mono text-muted-foreground">
        No chats yet. Send a message in the chat shell — your conversations
        are auto-saved here with smart titles like
        <span className="text-foreground"> "Quest: … · Apr 27 14:32"</span>.
      </div>
    );
  }
  return (
    <ol className="divide-y divide-border rounded-md border border-border bg-card overflow-hidden">
      {items.map((p, i) => (
        <li key={p.id}>
          <div className="group flex items-start gap-2 px-3 py-2.5 hover:bg-muted/40 transition-colors">
            <button
              type="button"
              onClick={() => onPick(p.id)}
              className="flex items-start gap-3 flex-1 min-w-0 text-left"
            >
            <div className="w-6 text-right shrink-0 pt-0.5 font-mono text-[11px] text-muted-foreground tabular-nums">
              {i + 1}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <div className="font-mono font-semibold text-sm text-foreground truncate flex-1 min-w-0">
                  {p.title}
                </div>
                {typeof p.message_count === "number" && p.message_count > 0 && (
                  <span className="font-mono text-[10px] text-muted-foreground tabular-nums shrink-0">
                    {p.message_count} msg
                  </span>
                )}
              </div>
              {p.teaser && (
                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                  {p.teaser}
                </div>
              )}
              {p.updated_at && (
                <div className="text-[10px] font-mono text-muted-foreground/80 mt-0.5">
                  {formatRelative(p.updated_at)}
                </div>
              )}
            </div>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void exportChatTranscript(p.id, p.title);
              }}
              title="Download transcript (.md)"
              aria-label={`Export ${p.title} as Markdown`}
              className="shrink-0 self-start mt-0.5 inline-flex items-center gap-1 rounded border border-border bg-background/60 px-1.5 py-1 font-mono text-[10px] text-muted-foreground hover:text-primary hover:border-primary opacity-60 group-hover:opacity-100 transition-all"
            >
              <Download className="w-3 h-3" />
              Export
            </button>
          </div>
        </li>
      ))}
    </ol>
  );
};

/** Fetch the full conversation row and trigger a Markdown download of
 *  the transcript. Includes any user/assistant turns plus a header
 *  with the smart title and timestamp so the file stands alone. Any
 *  generated artifacts inlined in assistant messages (markdown, code
 *  fences, image URLs) ride along verbatim. */
async function exportChatTranscript(id: string, title: string) {
  const { data, error } = await supabase
    .from("chat_conversations")
    .select("title, messages, model, last_activity_at, chat_type")
    .eq("id", id)
    .maybeSingle();
  if (error || !data) {
    toast.error("Could not export this chat");
    return;
  }
  const turns = Array.isArray(data.messages)
    ? (data.messages as Array<{ role: string; content: string }>)
    : [];
  const header = [
    `# ${data.title || title}`,
    "",
    `- Type: \`${(data as { chat_type?: string }).chat_type ?? "open"}\``,
    `- Model: \`${data.model ?? "unknown"}\``,
    `- Last activity: ${data.last_activity_at ?? ""}`,
    `- Exported: ${new Date().toISOString()}`,
    "",
    "---",
    "",
  ].join("\n");
  const body = turns
    .map((t) => {
      const who = t.role === "user" ? "🧑 You" : t.role === "assistant" ? "🤖 Assistant" : `⚙️ ${t.role}`;
      return `### ${who}\n\n${t.content ?? ""}\n`;
    })
    .join("\n");
  const md = header + body;
  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = (data.title || title || "chat")
    .replace(/[^\w\s.-]+/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 80);
  a.href = url;
  a.download = `${safeName || "chat"}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  toast.success("Transcript downloaded");
}

/** "2m ago", "3h ago", "Apr 27" — compact, no library dependency. */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86_400 * 7) return `${Math.floor(diffSec / 86_400)}d ago`;
  const d = new Date(then);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default ChatLibraryDialog;